import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import Navigation from "@/components/Navigation"
import Footer from "@/components/Footer"
import ScrollReveal from "@/components/ScrollReveal"
import WaveSeparator from "@/components/WaveSeparator"
import { Button } from "@/components/ui/je-button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { CheckCircle, Users, Calendar, Sparkles } from "lucide-react"
import { useLanguage } from "@/contexts/LanguageContext"
import { useSEO } from "@/hooks/use-seo"
import { useBotProtection } from "@/hooks/use-bot-protection"
import { Honeypot } from "@/components/ui/honeypot"

const RejoindreComite = () => {
  const { t } = useLanguage()
  
  useSEO({
    title: "Rejoindre le Comité — Junior Entreprise HEPIA",
    description: "Rejoignez le comité de la Junior Entreprise HEPIA et participez activement à la gestion de notre association étudiante.",
    canonical: 'https://je-hepia.ch/rejoindre-comite'
  })

  const [isSubmitted, setIsSubmitted] = useState(false)
  const { toast } = useToast()
  
  const {
    isSubmitting,
    handleHoneypotChange,
    submitForm,
    honeypotValue,
    enableHoneypot
  } = useBotProtection()

  const formSchema = z.object({
    section: z.string({
      required_error: "Veuillez sélectionner votre section",
    }),
    nom: z.string().min(2, {
      message: "Le nom doit contenir au moins 2 caractères",
    }),
    prenom: z.string().min(2, {
      message: "Le prénom doit contenir au moins 2 caractères",
    }),
    email: z.string().email({
      message: "Veuillez entrer une adresse email valide",
    }),
    suggested_slot: z.string({
      required_error: "Veuillez sélectionner un créneau de rencontre",
    }),
  })

  type FormValues = z.infer<typeof formSchema>

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      section: "",
      nom: "",
      prenom: "",
      email: "",
      suggested_slot: "",
    },
  })

  const onSubmit = async (values: FormValues) => {
    const data = {
      type: 'committee' as const,
      nom: values.nom,
      prenom: values.prenom,
      email: values.email,
      section: values.section,
      suggested_slot: values.suggested_slot,
    }

    const result = await submitForm(data)
    
    if (result.success) {
      setIsSubmitted(true)
      toast({
        title: "Succès !",
        description: result.message,
      })
    } else {
      toast({
        title: "Erreur",
        description: result.error,
        variant: "destructive",
      })
    }
  }

  const sections = [
    { value: "informatique", label: "Informatique" },
    { value: "electronique", label: "Électronique" },
    { value: "microtechniques", label: "Microtechniques" },
    { value: "genie-mecanique", label: "Génie Mécanique" },
    { value: "architecture", label: "Architecture" },
    { value: "genie-civil", label: "Génie Civil" },
    { value: "autre", label: "Autre filière" },
  ]

  // Créneaux prédéfinis pour les rencontres
  const timeSlots = [
    { value: "lundi-midi", label: "Lundi - Pause de midi (12h00-13h00)" },
    { value: "mardi-midi", label: "Mardi - Pause de midi (12h00-13h00)" },
    { value: "mercredi-midi", label: "Mercredi - Pause de midi (12h00-13h00)" },
    { value: "jeudi-midi", label: "Jeudi - Pause de midi (12h00-13h00)" },
    { value: "vendredi-midi", label: "Vendredi - Pause de midi (12h00-13h00)" },
    { value: "lundi-soir", label: "Lundi - Fin de journée (17h00-18h00)" },
    { value: "mardi-soir", label: "Mardi - Fin de journée (17h00-18h00)" },
    { value: "mercredi-soir", label: "Mercredi - Fin de journée (17h00-18h00)" },
    { value: "jeudi-soir", label: "Jeudi - Fin de journée (17h00-18h00)" },
    { value: "autre", label: "Autre créneau (nous vous contacterons)" },
  ]

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-background">
        <ScrollReveal />
        <Navigation />
        <main className="pt-16">
          <section className="py-24 bg-gradient-hero">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <CheckCircle className="w-24 h-24 text-je-red mx-auto mb-8" />
              <h1 className="text-section-title text-je-black mb-6">
                Demande envoyée !
              </h1>
              <p className="text-xl text-muted-foreground mb-8">
                Merci pour votre intérêt ! Un membre du comité vous contactera pour confirmer le créneau de rencontre.
              </p>
              <Button asChild size="lg" className="btn-modern">
                <a href="/">Retour à l'accueil</a>
              </Button>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <ScrollReveal />
      <Navigation />
      
      <main className="pt-16">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-hero">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-je-red/10 rounded-full mb-6">
              <Users className="w-10 h-10 text-je-red" />
            </div>
            <h1 className="text-section-title text-je-black mb-6">
              Rejoindre le Comité
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Participez activement à la gestion de la Junior Entreprise HEPIA et développez vos compétences en leadership.
            </p>
          </div>
        </section>

        <WaveSeparator />

        {/* Avantages Section */}
        <section className="py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              <div className="card-modern p-6 text-center">
                <div className="w-12 h-12 bg-je-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-6 h-6 text-je-red" />
                </div>
                <h3 className="font-bold text-je-black mb-2">Leadership</h3>
                <p className="text-muted-foreground text-sm">
                  Développez vos compétences en gestion et management
                </p>
              </div>
              <div className="card-modern p-6 text-center">
                <div className="w-12 h-12 bg-eth-purple/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-eth-purple" />
                </div>
                <h3 className="font-bold text-je-black mb-2">Réseau</h3>
                <p className="text-muted-foreground text-sm">
                  Élargissez votre réseau professionnel
                </p>
              </div>
              <div className="card-modern p-6 text-center">
                <div className="w-12 h-12 bg-eth-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-6 h-6 text-eth-green" />
                </div>
                <h3 className="font-bold text-je-black mb-2">Flexibilité</h3>
                <p className="text-muted-foreground text-sm">
                  Adaptez votre engagement à votre emploi du temps
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Formulaire Section */}
        <section className="py-24 bg-gradient-accent">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="card-modern p-8 md:p-12">
              <div className="text-center mb-12">
                <h2 className="text-card-title text-je-black mb-4">
                  Planifier une rencontre
                </h2>
                <p className="text-muted-foreground">
                  Remplissez ce formulaire et choisissez un créneau pour nous rencontrer
                </p>
              </div>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                  {/* Honeypot field */}
                  {enableHoneypot && (
                    <Honeypot 
                      value={honeypotValue}
                      onChange={handleHoneypotChange}
                    />
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Prénom */}
                    <FormField
                      control={form.control}
                      name="prenom"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-je-black font-semibold">
                            Prénom *
                          </FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Votre prénom" 
                              className="h-12 border-je-gray-medium focus:border-je-red"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Nom */}
                    <FormField
                      control={form.control}
                      name="nom"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-je-black font-semibold">
                            Nom *
                          </FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Votre nom" 
                              className="h-12 border-je-gray-medium focus:border-je-red"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Email */}
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-je-black font-semibold">
                            Email *
                          </FormLabel>
                          <FormControl>
                            <Input 
                              type="email"
                              placeholder="votre.email@hepia.ch" 
                              className="h-12 border-je-gray-medium focus:border-je-red"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Section */}
                    <FormField
                      control={form.control}
                      name="section"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-je-black font-semibold">
                            Section HEPIA *
                          </FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger className="h-12 border-je-gray-medium focus:border-je-red">
                                <SelectValue placeholder="Sélectionnez votre section" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-background border-je-gray-medium">
                              {sections.map((section) => (
                                <SelectItem 
                                  key={section.value} 
                                  value={section.value}
                                  className="hover:bg-je-red-light focus:bg-je-red-light"
                                >
                                  {section.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Créneau suggéré */}
                    <div className="md:col-span-2">
                      <FormField
                        control={form.control}
                        name="suggested_slot"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-je-black font-semibold">
                              Créneau de rencontre souhaité *
                            </FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger className="h-12 border-je-gray-medium focus:border-je-red">
                                  <SelectValue placeholder="Choisissez un créneau" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent className="bg-background border-je-gray-medium">
                                {timeSlots.map((slot) => (
                                  <SelectItem 
                                    key={slot.value} 
                                    value={slot.value}
                                    className="hover:bg-je-red-light focus:bg-je-red-light"
                                  >
                                    {slot.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Info box */}
                  <div className="bg-je-gray-light p-6 rounded-lg">
                    <h3 className="text-je-black font-semibold mb-3">
                      📅 Comment ça se passe ?
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      Après votre inscription, un membre du comité vous contactera pour confirmer le créneau. 
                      La rencontre aura lieu dans nos locaux à HEPIA et durera environ 30 minutes. 
                      C'est l'occasion de découvrir notre fonctionnement et de poser toutes vos questions.
                    </p>
                  </div>

                  {/* Bouton de soumission */}
                  <div className="text-center">
                    <Button
                      type="submit"
                      size="xl"
                      className="btn-modern min-w-[280px]"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Envoi en cours..." : "Envoyer ma demande"}
                    </Button>
                  </div>

                </form>
              </Form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}

export default RejoindreComite
